/**
 * useGeometryInteraction Hook - Unified Click/Double-Click/Double-Tap Handling
 *
 * Combines click and double-click handling for Line/Shape geometries into a single hook.
 * Implements delay mechanism to prevent single-click action during double-click.
 *
 * **Replaces**: useGeometryClick + useGeometryDoubleClick
 *
 * Features:
 * - Desktop: Single-click (delayed 200ms) and double-click detection
 * - Mobile: Single-tap and double-tap detection (long-press handled by context menu hooks)
 * - Delay mechanism prevents single-click/tap during double-click/tap
 * - Works with MapLibre GL JS event system
 * - Uses openTaskForEditWithGeometry helper (SSOT)
 *
 * Usage:
 * - Line/Shape: Pass interactiveLayerId (hitbox layer) and geometry object
 * - Marker: Use native HTML event listeners (not this hook)
 *
 * @module hooks/useGeometryInteraction
 * @example
 * ```typescript
 * // In LineRenderer or ShapeRenderer:
 * useGeometryInteraction({
 *   geometry,
 *   taskId,
 *   interactiveLayerId: `line-hitbox-${geometry.id}`,
 *   isPreview: false,
 * });
 * ```
 */

import { useCallback, useEffect, useRef } from 'react';
import { useMap } from '@vis.gl/react-maplibre';
import { useGeoMarkupStore } from '../store';
import {
  selectIsGeometryEditingActive,
  selectIsInteractionSuppressed,
} from '../store/selectors';
import { useTaskNavigation, useTaskOperations } from './';
import { openTaskForEditWithGeometry } from '../utils/geometryEditHelpers';
import { GeometryType } from '../types/geometry';
import { INTERACTION_SINGLE_CLICK_DELAY } from '../constants';
import type { MapLayerMouseEvent } from 'maplibre-gl';
import type { TaskGeometry } from '../types';
import { useMapLayerDoubleTap, useDelayedClick } from './gestures';

/**
 * Options for useGeometryInteraction hook
 */
export interface UseGeometryInteractionOptions {
  /** The geometry object containing all data */
  geometry: TaskGeometry;
  /** ID of the task this geometry belongs to */
  taskId: string;
  /** ID of the interactive layer (hitbox layer) for MapLibre events */
  interactiveLayerId: string;
  /** Whether this is a preview geometry (if true, no interactions) */
  isPreview?: boolean;
}

/**
 * Hook for handling unified geometry interactions (click + double-click)
 *
 * Registers event listeners on geometry's interactive layer.
 * Implements delay mechanism to distinguish between single and double-click.
 *
 * @param options - Hook configuration options
 */
export const useGeometryInteraction = ({
  geometry,
  taskId,
  interactiveLayerId,
  isPreview = false,
}: UseGeometryInteractionOptions) => {
  const { current: map } = useMap();

  // Task operations (SSOT)
  const { navigateToTaskDetails } = useTaskNavigation();
  const { openTaskForEdit } = useTaskOperations();

  // Zustand state
  const isGeometryEditingActive = useGeoMarkupStore(
    selectIsGeometryEditingActive
  );
  const activeTool = useGeoMarkupStore((s) => s.activeTool);

  // Zustand actions for geometry editing
  const loadGeometries = useGeoMarkupStore((s) => s.loadGeometries);
  const setActiveGeometry = useGeoMarkupStore((s) => s.setActiveGeometry);
  // Timer for click delay
  const clickTimerRef = useRef<number | null>(null);

  /**
   * Cancel pending single-click timer
   */
  const cancelPendingClick = useCallback(() => {
    if (clickTimerRef.current !== null) {
      window.clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
  }, []);

  /**
   * Open edit mode helper
   */
  const openEditMode = useCallback(() => {
    openTaskForEditWithGeometry(
      taskId,
      geometry.id,
      geometry.calculatedCenter,
      {
        allTasks: useGeoMarkupStore.getState().tasks.mapTasks,
        loadGeometries,
        setActiveGeometry,
        openTaskForEdit,
      }
    );
  }, [
    taskId,
    geometry.id,
    geometry.calculatedCenter,
    loadGeometries,
    setActiveGeometry,
    openTaskForEdit,
  ]);

  // Delayed click for single-tap navigation
  const {
    scheduleClick: scheduleDelayedClick,
    cancelPendingClick: cancelDelayedClick,
  } = useDelayedClick({
    delay: INTERACTION_SINGLE_CLICK_DELAY,
    onClick: () => {
      const task = useGeoMarkupStore
        .getState()
        .tasks.mapTasks.find((t) => t.id === taskId);
      navigateToTaskDetails(task?.shortId ?? taskId, geometry.calculatedCenter);
    },
  });

  // ========== DOUBLE-TAP (MOBILE) ==========
  // Uses unified useMapLayerDoubleTap hook with zoom protection
  useMapLayerDoubleTap(interactiveLayerId, {
    enabled: !isPreview && activeTool !== GeometryType.Selection,
    preventZoom: true,
    onDoubleTap: () => {
      // Skip if interactions are suppressed (panel just closed via click-outside)
      if (selectIsInteractionSuppressed(useGeoMarkupStore.getState())) {
        return;
      }
      // Cancel pending single-tap
      cancelDelayedClick();
      openEditMode();
    },
    onSingleTap: () => {
      // Skip if interactions are suppressed (panel just closed via click-outside)
      if (selectIsInteractionSuppressed(useGeoMarkupStore.getState())) {
        return;
      }
      // Only navigate if not in drawing mode
      const isFormOpen = useGeoMarkupStore.getState().tasks.formIsOpen;
      const tool = useGeoMarkupStore.getState().activeTool;

      if (!isFormOpen || tool === GeometryType.None) {
        scheduleDelayedClick();
      }
    },
  });

  /**
   * Setup desktop event listeners on interactive layer
   */
  useEffect(() => {
    // Don't register ANY listeners in Selection mode - useGeometrySelection handles it
    if (!map || isPreview || activeTool === GeometryType.Selection) return;

    // ===== SINGLE CLICK (with delay mechanism) =====
    /**
     * Handle single-click on geometry
     *
     * Implements 200ms delay to allow double-click detection.
     * If double-click occurs, pending single-click is cancelled.
     */
    const handleClick = (e: MapLayerMouseEvent) => {
      // Skip if interactions are suppressed (panel just closed via click-outside)
      if (selectIsInteractionSuppressed(useGeoMarkupStore.getState())) {
        return;
      }

      // DRAWING MODE: Don't intercept, let event propagate for drawing
      if (isGeometryEditingActive && activeTool !== GeometryType.None) {
        return; // Event propagates → drawing works
      }

      // Cancel any pending single-click (might be part of double-click!)
      cancelPendingClick();

      // Delay execution to allow double-click detection
      clickTimerRef.current = window.setTimeout(() => {
        // FREE ROAM or FORM CLOSED: Navigate to task details (preview)
        e.preventDefault();
        e.originalEvent.stopPropagation();
        const task = useGeoMarkupStore
          .getState()
          .tasks.mapTasks.find((t) => t.id === taskId);
        navigateToTaskDetails(
          task?.shortId ?? taskId,
          geometry.calculatedCenter
        );
        clickTimerRef.current = null;
      }, INTERACTION_SINGLE_CLICK_DELAY);
    };

    // ===== DOUBLE CLICK (desktop) =====
    /**
     * Handle double-click on geometry
     *
     * Opens task edit form and activates geometry.
     * Uses unified helper for consistency (SSOT pattern).
     * Guard inside openTaskForEditWithGeometry prevents unnecessary reinitialization.
     */
    const handleDoubleClick = (e: MapLayerMouseEvent) => {
      // Skip if interactions are suppressed (panel just closed via click-outside)
      if (selectIsInteractionSuppressed(useGeoMarkupStore.getState())) {
        return;
      }

      e.preventDefault();
      e.originalEvent.stopPropagation();

      // Cancel pending single-click (double-click detected!)
      cancelPendingClick();

      openEditMode();
    };

    // ===== Register event listeners on MapLibre interactive layer =====
    map.on('click', interactiveLayerId, handleClick);
    map.on('dblclick', interactiveLayerId, handleDoubleClick);

    // ===== Cleanup =====
    return () => {
      // Remove event listeners
      map.off('click', interactiveLayerId, handleClick);
      map.off('dblclick', interactiveLayerId, handleDoubleClick);

      // Clear pending click timer
      cancelPendingClick();
    };
  }, [
    map,
    interactiveLayerId,
    isPreview,
    isGeometryEditingActive,
    activeTool,
    taskId,
    geometry,
    navigateToTaskDetails,
    openEditMode,
    cancelPendingClick,
  ]);

  // No return value - hook manages all interactions internally
};
